// src/hooks/useUserPreferences.tsx
import { useEffect, useState } from "react";
import { ThemeConfig } from "antd";
import { loadUserPreferences, applyThemePreferences, UserPreferences, AccentColor } from "../utils/userPrefs/UserPrefs";
import { applyFullTheme } from '../utils/userPrefs/themeUtils';
import { reapplyBrandColor } from '../utils/branding/applyBranding';

/**
 * ⚠️ S7 (86d3w1g8u) — EVERY `applyFullTheme` await in this file is followed by
 * `reapplyBrandColor()`, INCLUDING BOTH ERROR FALLBACKS. `applyFullTheme` →
 * `applyCSSColorGroup` rewrites the seven `--mg-primary*` properties from the user's accent
 * group, so a whitelabelled clinic's colour is silently reverted by whichever of these paths
 * runs last. It is a no-op for a clinic with no brand colour.
 */

/**
 * Hook to load and apply user preferences across the application
 * Returns the theme config for Ant Design ConfigProvider
 */
export function useUserPreferences() {
  const [preferences, setPreferences] = useState<UserPreferences | null>(null);
  const [themeConfig, setThemeConfig] = useState<ThemeConfig | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);

  const loadTheme = async (userPrefs: UserPreferences) => {
    try {
      // Apply theme-related preferences and get Ant Design theme config
      applyThemePreferences(userPrefs);
      
      const config = await applyFullTheme(
        userPrefs.theme as 'light' | 'dark', 
        userPrefs.accentColor as AccentColor
      );
      reapplyBrandColor();

      setThemeConfig(config);
      setIsLoaded(true);
    } catch (error) {
      console.error('Failed to load theme:', error);
      // Try to load default theme as fallback
      try {
        const defaultConfig = await applyFullTheme('light', 'blue');
        reapplyBrandColor();
        setThemeConfig(defaultConfig);
      } catch (fallbackError) {
        console.error('Failed to load fallback theme:', fallbackError);
      }
      setIsLoaded(true);
    }
  };

  useEffect(() => {
    // Load preferences from storage
    const userPrefs = loadUserPreferences();
    setPreferences(userPrefs);
    
    // Load theme asynchronously
    loadTheme(userPrefs);

    // Listen for theme change events from preferences page
    const handleThemeChange = (event: CustomEvent) => {
      const newPrefs = event.detail;
      setPreferences(newPrefs);
      loadTheme(newPrefs);
    };

    window.addEventListener('themeChange', handleThemeChange as any);
    
    return () => {
      window.removeEventListener('themeChange', handleThemeChange as any);
    };
  }, []);

  return { preferences, themeConfig, isLoaded };
}

/**
 * Component to initialize preferences on app mount
 * This ensures theme is applied before any components render
 */
export const PreferencesInitializer = () => {
  useEffect(() => {
    const initializeTheme = async () => {
      try {
        // Load user preferences
        const preferences = loadUserPreferences();
        
        // Extract theme information
        const themeMode = preferences.theme as 'light' | 'dark';
        const accentColor = preferences.accentColor as AccentColor;
        
        // Apply theme classes immediately for any custom CSS
        applyThemePreferences(preferences);
        
        // Apply the full theme
        await applyFullTheme(themeMode, accentColor);
        reapplyBrandColor();

      } catch (error) {
        console.error('Failed to initialize theme:', error);

        // Fall back to default theme
        try {
          await applyFullTheme('light', 'blue');
          reapplyBrandColor();
          console.log('Fallback theme applied successfully');
        } catch (fallbackError) {
          console.error('Cannot load default theme:', fallbackError);
        }
      }
    };

    initializeTheme();
  }, []);
  
  return null;
};

export default PreferencesInitializer;